import { GET_ALL_WORTHS } from 'src/api/graphql/queries/worths.gql';
import { fetchList } from './fetchLists';
import { getAllCompanies } from './getAllCompanies';
import {
	pickFilters,
	prepareGraphqlFilters,
} from 'src/utils/helpers/prepareGraphqlFilters';
import {
	normalizeWorthMatch,
	normalizeWorthScore,
} from 'src/components/ui/DefaultForm/hooks/worthOptions';
import { mapGorkoStatusFilterToGraphql } from 'src/utils/mappers/companyGorkoStatus.mapper';

const WORTH_SCORE_FILTER_KEYS = ['wedding', 'birthday', 'newYear'];

const WORTH_MATCH_FILTER_KEYS = ['corporative', 'graduation'];

const WORTH_FILTER_KEYS = [
	'id',
	'company',
	'companyGorkoId',
	'required',
	'value',
	'valueAuto',
	'wedding',
	'weddingAuto',
	'birthday',
	'birthdayAuto',
	'corporative',
	'newYear',
	'newYearAuto',
	'graduation',
	'createdAt',
	'updatedAt',
];

const EMPTY_COMPANY_IDS = [-1];
const COMPANY_LOOKUP_LIMIT = 5000;

const asIdList = (value) =>
	(Array.isArray(value) ? value : [value])
		.map(Number)
		.filter((id) => Number.isFinite(id));

const normalizeListFilter = (condition) => {
	if (!condition || typeof condition !== 'object') return condition;

	const next = { ...condition };

	for (const op of ['eq', 'neq', 'contains', 'notContains']) {
		if (next[op] != null && !Array.isArray(next[op])) {
			next[op] = [next[op]];
		}
	}

	return next;
};

const fetchCompanyIds = async (filters) => {
	const { items } = await getAllCompanies({
		page: 1,
		limit: COMPANY_LOOKUP_LIMIT,
		filters,
	});

	return (items || []).map((item) => item.id).filter(Boolean);
};

const applyCompanyConstraint = (prepared, constraint) => {
	if (!constraint) return;

	if (constraint.isNull != null) {
		prepared.company = { isNull: constraint.isNull };
		return;
	}

	if (constraint.eq) {
		const nextIds = asIdList(constraint.eq);

		if (prepared.company?.eq) {
			const current = asIdList(prepared.company.eq);
			const ids = current.filter((id) => nextIds.includes(id));
			prepared.company = { eq: ids.length ? ids : EMPTY_COMPANY_IDS };
			return;
		}

		if (prepared.company?.neq) {
			const excluded = new Set(asIdList(prepared.company.neq));
			const ids = nextIds.filter((id) => !excluded.has(id));
			prepared.company = { eq: ids.length ? ids : EMPTY_COMPANY_IDS };
			return;
		}

		prepared.company = {
			eq: nextIds.length ? nextIds : EMPTY_COMPANY_IDS,
		};
		return;
	}

	if (constraint.neq) {
		const excluded = asIdList(constraint.neq);

		if (!excluded.length) return;

		if (prepared.company?.eq) {
			const ids = asIdList(prepared.company.eq).filter(
				(id) => !excluded.includes(id),
			);
			prepared.company = { eq: ids.length ? ids : EMPTY_COMPANY_IDS };
			return;
		}

		prepared.company = { neq: excluded };
	}
};

const resolveCompanyNameConstraint = async (nameCondition) => {
	if (!nameCondition || typeof nameCondition !== 'object') return null;

	if (nameCondition.isNull != null) {
		return { isNull: nameCondition.isNull };
	}

	const [op, value] = Object.entries(nameCondition)[0] ?? [];
	if (!op || value == null) return null;

	const lookupOp =
		op === 'neq' ? 'eq' : op === 'notContains' ? 'contains' : op;
	const exclude = op === 'neq' || op === 'notContains';

	const ids = await fetchCompanyIds({
		name: normalizeListFilter({ [lookupOp]: value }),
	});

	if (exclude) {
		return ids.length ? { neq: ids } : null;
	}

	return { eq: ids.length ? ids : EMPTY_COMPANY_IDS };
};

const normalizeEnumFilter = (condition, normalize) => {
	if (!condition || typeof condition !== 'object') return condition;

	const next = { ...condition };

	if (next.eq != null) {
		next.eq = (Array.isArray(next.eq) ? next.eq : [next.eq])
			.map(normalize)
			.filter(Boolean);
	}

	if (next.neq != null) {
		next.neq = (Array.isArray(next.neq) ? next.neq : [next.neq])
			.map(normalize)
			.filter(Boolean);
	}

	return next;
};

const normalizeWorthScoreFilter = (condition) =>
	normalizeEnumFilter(condition, normalizeWorthScore);

const normalizeWorthMatchFilter = (condition) =>
	normalizeEnumFilter(condition, normalizeWorthMatch);

export const getAllWorths = async ({ page, limit, sort, filters }) => {
	const prepared = pickFilters(prepareGraphqlFilters(filters), [
		...WORTH_FILTER_KEYS,
		'companyName',
		'gorkoStatus',
	]);

	if (prepared.companyName) {
		const constraint = await resolveCompanyNameConstraint(
			prepared.companyName,
		);
		delete prepared.companyName;
		applyCompanyConstraint(prepared, constraint);
	}

	if (prepared.gorkoStatus) {
		const gorkoFilters = mapGorkoStatusFilterToGraphql(
			prepared.gorkoStatus,
		);
		delete prepared.gorkoStatus;

		if (Object.keys(gorkoFilters).length) {
			const ids = await fetchCompanyIds(gorkoFilters);
			applyCompanyConstraint(prepared, {
				eq: ids.length ? ids : EMPTY_COMPANY_IDS,
			});
		}
	}

	// WorthFilterInput не содержит isGorko* — только company
	delete prepared.isGorkoCommission;
	delete prepared.isGorkoAbonent;
	delete prepared.isGorkoDeleted;
	delete prepared.isGorkoHidden;
	delete prepared.gorkoStatus;
	delete prepared.companyName;

	for (const key of WORTH_SCORE_FILTER_KEYS) {
		if (prepared[key]) {
			prepared[key] = normalizeWorthScoreFilter(prepared[key]);
		}
	}

	for (const key of WORTH_MATCH_FILTER_KEYS) {
		if (prepared[key]) {
			prepared[key] = normalizeWorthMatchFilter(prepared[key]);
		}
	}

	return fetchList({
		query: GET_ALL_WORTHS,
		variables: {
			page,
			limit,
			sort,
			filters: pickFilters(prepared, WORTH_FILTER_KEYS),
		},
		addPerPage: true,
	});
};
